import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Mail, UserPlus, Users, ChevronDown } from "lucide-react";
import { Page } from "@/components/layout";
import { ContentCard } from "@/components/ui/ContentCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useLeaderScope } from "@/hooks/useLeaderScope";
import { cn } from "@/lib/utils";
import { emailMatchesDistrictDomain } from "@/lib/inviteEmailDomain";
import {
  cancelInvite,
  getInvitableSchools,
  getLicenseSummary,
  getPendingInvites,
  getSchoolDistrictDomain,
  resendInvite,
  sendInvite,
  type SchoolPickerOption,
} from "@/services/inviteTeammatesService";
import {
  INVITE_POOL_LABEL,
  INVITE_ROLE_LABEL,
  INVITE_ROLE_TO_POOL,
  type InviteRole,
  type LicenseSummary,
  type PendingInvite,
} from "@/types/inviteTeammates";
import type { LeaderScope } from "@/hooks/useLeaderScope";
import { LicenseSummaryCard, PendingInvitesCard } from "./InviteAdminCards";

const ALL_ROLES: InviteRole[] = ["teacher", "support_staff", "leader"];

export const DistrictInviteAccordionPage = () => {
  const { toast } = useToast();
  const { userData } = useAuth();
  const { scope, isLoading: scopeLoading } = useLeaderScope();

  const [licenseSummary, setLicenseSummary] = useState<LicenseSummary | null>(null);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [schoolOptions, setSchoolOptions] = useState<SchoolPickerOption[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);

  const refresh = useCallback(async () => {
    if (scopeLoading) return;
    setIsLoadingData(true);
    try {
      const [summary, invites, schools] = await Promise.all([
        getLicenseSummary(scope),
        getPendingInvites(scope),
        getInvitableSchools(scope),
      ]);
      setLicenseSummary(summary);
      setPendingInvites(invites);
      setSchoolOptions(schools);
    } finally {
      setIsLoadingData(false);
    }
  }, [scope, scopeLoading]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <Page.Container>
      <Page.Header className="space-y-2">
        <h1 className="font-literata text-3xl font-semibold text-white md:text-4xl">Invite Teammates</h1>
        <p className="max-w-2xl font-inter text-sm leading-relaxed text-white/80 md:text-base">
          Choose a school to send invites or manage pending requests for that building.
        </p>
      </Page.Header>

      <Page.Content className="space-y-6">
        {scopeLoading || isLoadingData || !licenseSummary ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-white/70" aria-hidden />
          </div>
        ) : (
          <>
            <LicenseSummaryCard summary={licenseSummary} />

            <ContentCard className="p-0">
              <ul className="divide-y divide-stone-200">
                {schoolOptions.map((school) => (
                  <SchoolInviteAccordionRow
                    key={school.schoolId}
                    scope={scope}
                    school={school}
                    licenseSummary={licenseSummary}
                    pendingInvites={pendingInvites}
                    inviterEmail={userData?.email ?? null}
                    onRefresh={refresh}
                  />
                ))}
                {schoolOptions.length === 0 && (
                  <li className="px-5 py-8 text-center font-inter text-sm text-stone-500">No schools available.</li>
                )}
              </ul>
            </ContentCard>
          </>
        )}
      </Page.Content>
    </Page.Container>
  );
};

function SchoolInviteAccordionRow({
  scope,
  school,
  licenseSummary,
  pendingInvites,
  inviterEmail,
  onRefresh,
}: {
  scope: LeaderScope;
  school: SchoolPickerOption;
  licenseSummary: LicenseSummary;
  pendingInvites: PendingInvite[];
  inviterEmail: string | null;
  onRefresh: () => Promise<void>;
}) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [roleInput, setRoleInput] = useState<InviteRole>("teacher");
  const [formError, setFormError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [busyInviteId, setBusyInviteId] = useState<string | null>(null);
  const [districtDomain, setDistrictDomain] = useState<string | null>(null);

  const forSchool = useMemo(
    () => pendingInvites.filter((i) => i.schoolId === school.schoolId),
    [pendingInvites, school.schoolId]
  );

  useEffect(() => {
    let cancelled = false;
    void getSchoolDistrictDomain(school.schoolId).then((info) => {
      if (!cancelled) setDistrictDomain(info?.domain ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [school.schoolId]);

  const selectedPool = INVITE_ROLE_TO_POOL[roleInput];
  const poolForSelected = licenseSummary.pools[selectedPool];
  const isPoolFull = Boolean(poolForSelected && poolForSelected.used >= poolForSelected.total);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (districtDomain && !emailMatchesDistrictDomain(emailInput, districtDomain)) {
      setFormError(`Must use an email ending in @${districtDomain}.`);
      return;
    }

    setIsSending(true);
    try {
      const result = await sendInvite(
        scope,
        {
          email: emailInput,
          role: roleInput,
          schoolId: school.schoolId,
          schoolName: school.schoolName,
        },
        inviterEmail
      );

      if (!result.ok) {
        setFormError(result.errorMessage ?? "Could not send invite.");
        toast({
          title: "Invite not sent",
          description: result.errorMessage ?? "Could not send invite.",
          variant: "destructive",
        });
        return;
      }

      setEmailInput("");
      toast({
        title: "Invite sent",
        description: `${result.invite!.email} will get an email to join ${school.schoolName}.`,
      });
      await onRefresh();
    } finally {
      setIsSending(false);
    }
  };

  const handleCancel = async (invite: PendingInvite) => {
    setBusyInviteId(invite.id);
    try {
      const result = await cancelInvite(scope, invite.id);
      if (result.ok) {
        toast({
          title: "Invite cancelled",
          description: `${invite.email} will no longer receive a join link.`,
        });
        await onRefresh();
      } else {
        toast({
          title: "Could not cancel invite",
          description: result.errorMessage ?? "Try again.",
          variant: "destructive",
        });
      }
    } finally {
      setBusyInviteId(null);
    }
  };

  const handleResend = async (invite: PendingInvite) => {
    setBusyInviteId(invite.id);
    try {
      const result = await resendInvite(scope, invite.id);
      if (result.ok) {
        toast({
          title: "Invite resent",
          description: `Sent a fresh join link to ${invite.email}.`,
        });
        await onRefresh();
      } else {
        toast({
          title: "Could not resend invite",
          description: result.errorMessage ?? "Try again.",
          variant: "destructive",
        });
      }
    } finally {
      setBusyInviteId(null);
    }
  };

  return (
    <li>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="flex w-full items-center gap-4 px-4 py-4 text-left transition-colors hover:bg-stone-50 md:px-6 md:py-5">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
            <Users className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-literata text-base font-semibold text-stone-900 md:text-lg">{school.schoolName}</p>
            <p className="font-inter text-xs text-stone-500 md:text-sm">Send and track invites for this school.</p>
          </div>
          <ChevronDown
            aria-hidden
            className={cn(
              "h-5 w-5 shrink-0 text-stone-400 transition-transform duration-200",
              open && "rotate-180"
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="border-t border-stone-100 bg-stone-50/50 px-4 py-5 md:px-6 md:py-6">
          <div className="mx-auto max-w-3xl space-y-5">
            <ContentCard className="space-y-5 p-5 md:p-6">
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                  <UserPlus className="h-5 w-5" aria-hidden />
                </span>
                <div className="min-w-0">
                  <h2 className="font-literata text-base font-semibold text-stone-900 md:text-lg">Send a new invite</h2>
                  <p className="mt-1 font-inter text-xs text-stone-600 md:text-sm">
                    Leader invites use a separate seat pool from teachers and support staff.
                  </p>
                </div>
              </div>

              <form className="grid gap-4 md:grid-cols-[2fr,1fr,auto] md:items-end" onSubmit={handleSubmit}>
                <label className="flex min-w-0 flex-col gap-1.5">
                  <span className="font-inter text-xs font-medium uppercase tracking-wide text-stone-500">Email</span>
                  <div className="relative">
                    <Mail
                      className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400"
                      aria-hidden
                    />
                    <Input
                      type="email"
                      autoComplete="off"
                      required
                      placeholder="teammate@school.org"
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  {districtDomain && (
                    <p className="font-inter text-xs text-stone-500">Must use @{districtDomain}</p>
                  )}
                </label>

                <label className="flex min-w-0 flex-col gap-1.5">
                  <span className="font-inter text-xs font-medium uppercase tracking-wide text-stone-500">Role</span>
                  <Select value={roleInput} onValueChange={(v) => setRoleInput(v as InviteRole)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ALL_ROLES.map((role) => (
                        <SelectItem key={role} value={role}>
                          {INVITE_ROLE_LABEL[role]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>

                <Button
                  type="submit"
                  disabled={isSending || isPoolFull}
                  className="h-10 bg-[#1f3a30] text-white hover:bg-[#1f3a30]/90 md:w-auto"
                >
                  {isSending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : (
                    <UserPlus className="h-4 w-4" aria-hidden />
                  )}
                  Send invite
                </Button>

                {(formError || isPoolFull) && (
                  <p className="font-inter text-xs text-rose-600 md:col-span-3">
                    {formError ??
                      (isPoolFull
                        ? `${INVITE_POOL_LABEL[selectedPool]} pool is full. Cancel a pending invite or contact support to add seats.`
                        : null)}
                  </p>
                )}
              </form>
            </ContentCard>

            <PendingInvitesCard
              invites={forSchool}
              busyInviteId={busyInviteId}
              onCancel={handleCancel}
              onResend={handleResend}
              showSchool={false}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </li>
  );
}
